// src/hooks/useSimulation.js
// --- UPDATE: v1.0 — Digital Twin simulation state machine
//   • run(): aborts any in-flight run, posts to /simulations/run, indexes results
//   • progress: listens to the engine's `${scenario}_progress` WebSocket events
//     (ensemble runs) with ONE stable subscription for the life of the hook
//   • external broadcasts: surfaces simulations started by other operators
//   • playback: hour cursor over the storm timeline (hurricane / flood forecast)
//   • derived maps: recordsById, stateById — O(1) lookups for the 3D scene
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import webSocketService from '../services/websocketService';
import { runSimulation as apiRun, getRun as apiGetRun } from '../services/simulationService';
import { buildRequest } from '../components/digital_twin/simulationScenarios';

const STATUS = Object.freeze({ IDLE: 'idle', RUNNING: 'running', DONE: 'done', ERROR: 'error' });

export default function useSimulation({ onComplete, onError } = {}) {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [result, setResult] = useState(null);     // engine result (data)
  const [meta, setMeta] = useState(null);         // { run_id, scenario, title, created_at, duration_ms, values, request }
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null); // 0..100 for ensemble runs
  const [elapsedMs, setElapsedMs] = useState(0);
  const [playbackHour, setPlaybackHour] = useState(null); // null = show peak/final state
  const [externalRun, setExternalRun] = useState(null);

  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const statusRef = useRef(status);
  const scenarioRef = useRef(null);
  const ownRunUntilRef = useRef(0);
  const callbacksRef = useRef({ onComplete, onError });
  statusRef.current = status;
  callbacksRef.current = { onComplete, onError };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  // Elapsed timer while running
  useEffect(() => {
    if (status !== STATUS.RUNNING) return undefined;
    const started = Date.now();
    setElapsedMs(0);
    const id = setInterval(() => setElapsedMs(Date.now() - started), 250);
    return () => clearInterval(id);
  }, [status]);

  // One WebSocket subscription for progress + external broadcasts
  useEffect(() => {
    const handler = (msg) => {
      const m = msg?.payload || msg?.data || msg;
      if (!m || !m.event_type || !mountedRef.current) return;
      if (String(m.event_type).endsWith('_progress')) {
        if (statusRef.current === STATUS.RUNNING && m.event_type === `${scenarioRef.current}_progress`) {
          setProgress(Number(m.progress_pct) || 0);
        }
        return;
      }
      // Our own run broadcasts too — ignore anything arriving while/just after we ran
      if (statusRef.current === STATUS.RUNNING || Date.now() < ownRunUntilRef.current) return;
      setExternalRun({
        scenario: m.event_type,
        timestamp: m.timestamp || new Date().toISOString(),
        summary: m.summary || null,
        originAssetId: m.origin_asset_id ?? null,
        affectedCount: Array.isArray(m.affected_network) ? m.affected_network.length : null,
      });
    };
    try {
      webSocketService.connect?.();
      webSocketService.subscribe('simulation_active', handler);
    } catch (_) { /* socket layer reports its own errors */ }
    return () => {
      try { webSocketService.unsubscribe?.('simulation_active', handler); } catch (_) { /* noop */ }
    };
  }, []);

  /**
   * @param {object} scenario  entry from SCENARIOS
   * @param {object} values    UI parameter values
   * @param {object} ctx       { assetId, selectedAsset, centroid }
   */
  const run = useCallback(async (scenario, values, ctx = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    scenarioRef.current = scenario.id;

    const request = buildRequest(scenario, values, ctx);
    setStatus(STATUS.RUNNING);
    setError(null);
    setProgress(null);
    setPlaybackHour(null);

    try {
      const body = await apiRun(request, { signal: controller.signal });
      if (!mountedRef.current || controller.signal.aborted) return null;
      ownRunUntilRef.current = Date.now() + 5000;
      setResult(body.data);
      setMeta({
        run_id: body.run_id, scenario: body.scenario, title: body.title || scenario.title,
        created_at: body.created_at, duration_ms: body.duration_ms,
        values, request, ignored: body.ignored_parameters || null,
      });
      setStatus(STATUS.DONE);
      setProgress(100);
      callbacksRef.current.onComplete?.(body);
      return body;
    } catch (err) {
      if (!mountedRef.current) return null;
      if (err.cancelled || controller.signal.aborted) {
        setStatus(STATUS.IDLE);
        return null;
      }
      setError(err);
      setStatus(STATUS.ERROR);
      callbacksRef.current.onError?.(err);
      return null;
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus((s) => (s === STATUS.RUNNING ? STATUS.IDLE : s));
  }, []);

  const clear = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setResult(null);
    setMeta(null);
    setError(null);
    setProgress(null);
    setPlaybackHour(null);
    setStatus(STATUS.IDLE);
  }, []);

  /** Re-open a stored run (e.g. from the run history or another operator). */
  const loadRun = useCallback(async (runId) => {
    setStatus(STATUS.RUNNING);
    try {
      const body = await apiGetRun(runId, { limit: 5000 });
      if (!mountedRef.current) return;
      setResult(body.data);
      setMeta({ run_id: body.run_id, scenario: body.scenario, title: body.title, created_at: body.created_at });
      setStatus(STATUS.DONE);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err);
      setStatus(STATUS.ERROR);
    }
  }, []);

  const dismissExternal = useCallback(() => setExternalRun(null), []);

  // ── Derived ──────────────────────────────────────────────────────────────
  const recordsById = useMemo(() => {
    const m = new Map();
    for (const r of result?.affectedNodes || []) m.set(String(r.asset_id), r);
    return m;
  }, [result]);

  const timeline = useMemo(() => result?.timeline || [], [result]);
  const hourRange = useMemo(() => (timeline.length
    ? { min: timeline[0].hour, max: timeline[timeline.length - 1].hour, step: timeline.length > 1 ? timeline[1].hour - timeline[0].hour : 1 }
    : null), [timeline]);

  /** Record state at the playback hour (falls back to final state). */
  const stateAt = useCallback((r, hour) => {
    if (!r) return null;
    if (hour == null || r.evaluated === false) return r.state;
    const ttf = r.time_to_flood_hours;
    const flooded = ttf != null && hour >= ttf && hour <= ttf + (r.hours_flooded || 0);
    const peakAt = r.hours_from_landfall_of_peak_wind;
    const damaged = peakAt != null ? hour >= peakAt : true;
    if (!damaged && !flooded) return 'ok';
    if (flooded && r.state === 'ok') return 'warning';
    return r.state;
  }, []);

  const stateById = useMemo(() => {
    const m = new Map();
    for (const [id, r] of recordsById) {
      const s = stateAt(r, playbackHour);
      if (s && s !== 'ok') m.set(id, s);
    }
    return m;
  }, [recordsById, playbackHour, stateAt]);

  const timelineFrame = useMemo(() => {
    if (playbackHour == null || !timeline.length) return null;
    let best = timeline[0];
    for (const t of timeline) if (Math.abs(t.hour - playbackHour) < Math.abs(best.hour - playbackHour)) best = t;
    return best;
  }, [timeline, playbackHour]);

  return {
    status, isRunning: status === STATUS.RUNNING, isActive: !!result,
    result, meta, error, progress, elapsedMs,
    run, cancel, clear, loadRun,
    recordsById, stateById, stateAt,
    timeline, hourRange, playbackHour, setPlaybackHour, timelineFrame,
    externalRun, dismissExternal,
  };
}

export { STATUS as SIMULATION_STATUS };
// --- END UPDATE